import {clamp, cloneDeep, isArray, isBoolean, isEmpty, isNil, isNumber, isPlainObject, isString, max, omit, sortBy, sumBy, uniq} from 'lodash';
import {ChartSpec, ChartSpecSeries, ChartType} from '../../../cmp/chartSpec/ChartSpecTypes';
import {leafColumns} from '../../../cmp/gridSpec/GridSpecOps';
import {GridSpec, GridSpecColumn} from '../../../cmp/gridSpec/GridSpecTypes';
import {SpecContext} from '../../../cmp/spec/SpecModel';
import {applyEach, check, OpError} from '../../../cmp/spec/SpecOps';
import {ApplySpecOpsResult, RejectedSpecOp} from '../../../cmp/spec/SpecTypes';
import {ResearchDataFilter, ResearchDataset, ResearchDataSource, ResearchFilterOp, ResearchWidgetKind, ResearchWidgetState} from '../../../data/ResearchTypes';
import {chartSeriesFor, chartSpecFor, defaultChartSpec, defaultGridSpec, gridSpecFor, isMeasure, TICKER_FIELD, unitAxis} from '../ResearchDatasets';
import {DEFAULT_WIDGET_SIZE} from '../ResearchPresets';
import {
    BRIEF_CHART_TYPES,
    DASHBOARD_SPEC_VERSION,
    DashboardCapabilities,
    DashboardSpec,
    DashboardWidget,
    DashboardWidgetLayout,
    DescribedPreset,
    DescribedWidget,
    MAX_WIDGET_HEIGHT,
    MIN_WIDGET_SIZE,
    WIDGET_KINDS,
    WidgetBrief
} from './DashboardSpecTypes';

/**
 * DashboardOp - the vocabulary for changing a DashboardSpec, applied by `applyOps()`.
 *
 * Ops name widgets by id and data by dataset and field name. A widget is given as a WidgetBrief: the
 * reducer builds its GridSpec / ChartSpec from the brief, and keeps a widget's existing spec wherever
 * a change leaves it valid.
 */
export type WidgetPlace = Partial<DashboardWidgetLayout>;

/** Changes to what a widget shows - a `null` value removes the setting. The kind can't be changed. */
export type WidgetChanges = {[K in keyof Omit<WidgetBrief, 'kind'>]?: WidgetBrief[K] | null};

export type DashboardOp =
    | {op: 'clearWidgets'}
    | {op: 'addWidget'; widget: WidgetBrief & WidgetPlace}
    | ({op: 'addPreset'; preset: string; title?: string} & WidgetPlace)
    | {op: 'updateWidget'; id: string; changes: WidgetChanges}
    | ({op: 'moveWidget'; id: string} & WidgetPlace)
    | {op: 'removeWidget'; id: string}
    | {op: 'replaceSpec'; spec: DashboardSpec};

export type DashboardContext = SpecContext<DashboardCapabilities>;
export type RejectedDashboardOp = RejectedSpecOp<DashboardOp>;
export type ApplyDashboardOpsResult = ApplySpecOpsResult<DashboardSpec, DashboardOp>;

const FILTER_OPS: ResearchFilterOp[] = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'isNull', 'isNotNull', 'contains'],
    CHANGE_KEYS: (keyof WidgetChanges)[] = ['title', 'purpose', 'dataset', 'latest', 'transpose', 'filters', 'fields', 'chartType'],
    PLACE_KEYS: (keyof DashboardWidgetLayout)[] = ['x', 'y', 'w', 'h'];

/**
 * Apply ops to a spec, returning a new spec. Each op is validated against the datasets and the
 * canvas; invalid ops are skipped and reported, the rest still apply.
 */
export function applyOps(spec: DashboardSpec, ops: DashboardOp[], ctx: DashboardContext): ApplyDashboardOpsResult {
    // Widgets added without an x and y are laid out together once all the ops are in
    const flowed = new Set<string>(),
        result = applyEach(spec, ops, (draft, op) => applyOp(draft, op, ctx, flowed));
    if (flowed.size) arrangeFlowed(result.spec, flowed, ctx);
    return result;
}

export function normalizeSpec(spec: Partial<DashboardSpec>, defaultSpec: DashboardSpec): DashboardSpec {
    return {version: DASHBOARD_SPEC_VERSION, widgets: cloneDeep(spec?.widgets ?? defaultSpec.widgets)};
}

/** Short, human-readable description of ops - `titles` gives the names of the widgets and ready-made widgets the ops refer to by id. */
export function describeOps(ops: DashboardOp[], titles?: Map<string, string>): string {
    const name = (id: string) => titles?.get(id) ?? id;
    return ops
        .map(o => {
            switch (o.op) {
                case 'clearWidgets':
                    return 'remove all';
                case 'addWidget':
                    return `add ${o.widget?.title ?? o.widget?.dataset}`;
                case 'addPreset':
                    return `add ${o.title ?? name(o.preset)}`;
                case 'updateWidget':
                    return `update ${name(o.id)}`;
                case 'moveWidget':
                    return `move ${name(o.id)}`;
                case 'removeWidget':
                    return `remove ${name(o.id)}`;
                case 'replaceSpec':
                    return 'replace dashboard';
            }
        })
        .join(', ');
}

//------------------------
// What the assistant is shown
//------------------------
/** What a widget shows, read back from its saved state - the fields are those of its spec, or of the default one. */
export function briefOf(widget: DashboardWidget, ctx: DashboardContext): WidgetBrief {
    const {kind, title, state} = widget,
        source = state?.source,
        ret: WidgetBrief = {kind, title, dataset: source?.dataset};
    if (!source) return ret;

    if (source.latest) ret.latest = true;
    if (source.transpose) ret.transpose = true;
    if (!isEmpty(source.filters)) ret.filters = source.filters;
    if (state.purpose) ret.purpose = state.purpose;

    const dataset = findDataset(source.dataset, ctx);
    if (!dataset) return ret;

    if (source.transpose) {
        if (!isEmpty(source.fields)) ret.fields = source.fields;
    } else if (kind === 'grid') {
        const spec = (state.spec as GridSpec) ?? defaultGridSpec(dataset, source);
        if (isArray(spec?.columns))
            ret.fields = leafColumns(spec)
                .filter(c => !c.hidden)
                .map(c => c.field);
    } else {
        const spec = (state.spec as ChartSpec) ?? defaultChartSpec(dataset, source);
        if (isArray(spec?.series)) {
            ret.fields = spec.series.filter(s => !s.hidden).map(s => s.field);
            ret.chartType = spec.type as WidgetBrief['chartType'];
        }
    }
    return ret;
}

/** The dashboard as sent to the assistant: each widget's id, place and brief, in reading order. */
export function describeDashboard(spec: DashboardSpec, ctx: DashboardContext): {version: number; widgets: DescribedWidget[]} {
    const widgets = sortBy(spec.widgets, [w => w.layout.y, w => w.layout.x]).map(w => {
        const {kind, title, ...brief} = briefOf(w, ctx);
        return {id: w.id, kind, title, ...w.layout, ...brief};
    });
    return {version: spec.version, widgets};
}

export function describePresets(ctx: DashboardContext): DescribedPreset[] {
    return ctx.capabilities.presets.map(p => ({id: p.id, title: p.title, kind: p.kind, dataset: p.state.source.dataset, w: p.width, h: p.height}));
}

//------------------------
// Reducer
//------------------------
/** Applies an op, or throws to reject it. Returns a warning when it left out part of what the op asked for. */
function applyOp(spec: DashboardSpec, o: DashboardOp, ctx: DashboardContext, flowed: Set<string>): string | void {
    switch (o.op) {
        case 'clearWidgets':
            spec.widgets = [];
            return;
        case 'addWidget':
            return addWidget(spec, o.widget, ctx, flowed);
        case 'addPreset':
            return addPreset(spec, o.preset, o.title, o, ctx, flowed);
        case 'updateWidget':
            return updateWidget(spec, o.id, o.changes, ctx);
        case 'moveWidget':
            moveWidget(spec, o.id, o, ctx);
            flowed.delete(o.id);
            return;
        case 'removeWidget':
            spec.widgets.splice(spec.widgets.indexOf(requireWidget(spec, o.id)), 1);
            return;
        case 'replaceSpec':
            return replaceSpec(spec, o.spec, ctx);
        default:
            throw new OpError(`Unknown op '${(o as any).op}'`);
    }
}

function addWidget(spec: DashboardSpec, brief: WidgetBrief & WidgetPlace, ctx: DashboardContext, flowed: Set<string>) {
    check(isPlainObject(brief), 'widget must be an object');
    const {kind} = brief;
    check(WIDGET_KINDS.includes(kind), `kind must be one of ${WIDGET_KINDS.join(', ')}`);

    const dataset = requireDataset(brief.dataset, ctx),
        title = validateTitle(brief.title) ?? dataset.title,
        {fields, warning} = validateFields(brief.fields, dataset, kind, title),
        source = buildSource({...brief, fields}, dataset),
        widgetSpec = specFor(kind, dataset, source, fields, validateChartType(brief.chartType, kind));

    insert(
        spec,
        {kind, title, state: withPurpose(widgetSpec ? {source, spec: widgetSpec} : {source}, validatePurpose(brief.purpose))},
        brief,
        DEFAULT_WIDGET_SIZE,
        ctx,
        flowed
    );
    return warning;
}

function addPreset(spec: DashboardSpec, presetId: string, title: string, place: WidgetPlace, ctx: DashboardContext, flowed: Set<string>) {
    const preset = ctx.capabilities.presets.find(p => p.id === presetId);
    check(preset, `Unknown ready-made widget '${presetId}'`);

    insert(spec, {kind: preset.kind, title: validateTitle(title) ?? preset.title, state: cloneDeep(preset.state)}, place, preset, ctx, flowed);
}

function updateWidget(spec: DashboardSpec, id: string, changes: WidgetChanges, ctx: DashboardContext) {
    const widget = requireWidget(spec, id),
        {kind} = widget;
    check(isPlainObject(changes) && !isEmpty(changes), 'changes must be an object');
    check(!('kind' in changes), "A widget's kind cannot be changed - remove it and add another");
    Object.keys(changes).forEach(k => check(CHANGE_KEYS.includes(k as any), `Unknown widget setting '${k}'`));

    const {title, purpose, ...data} = changes,
        newPurpose = 'purpose' in changes ? validatePurpose(purpose) : undefined;
    if (!isNil(title)) widget.title = validateTitle(title);
    if (isEmpty(data)) {
        if ('purpose' in changes) widget.state = withPurpose(widget.state ?? {}, newPurpose);
        return;
    }

    // The widget as it is, with the changes on top - a new dataset starts over, as its fields are others
    const current = briefOf(widget, ctx),
        prevSource = widget.state?.source,
        prevSpec = widget.state?.spec,
        datasetChanged = !isNil(data.dataset) && data.dataset !== current.dataset,
        next: WidgetBrief = datasetChanged ? {kind, dataset: data.dataset} : {...current};
    Object.entries(data).forEach(([key, v]) => {
        if (v === null) delete next[key];
        else next[key] = v;
    });
    check(next.dataset, 'This widget shows no data yet - set its dataset');

    // Fields the widget lists only because they are its default aren't carried over to a new shape
    const dataset = requireDataset(next.dataset, ctx),
        fieldsGiven = 'fields' in data,
        typeGiven = 'chartType' in data,
        wasExplicit = !!prevSpec || !isEmpty(prevSource?.fields),
        {fields, warning} = fieldsGiven || (!datasetChanged && wasExplicit) ? validateFields(next.fields, dataset, kind, widget.title) : NO_FIELDS,
        chartType = typeGiven ? validateChartType(next.chartType, kind) : !datasetChanged && prevSpec ? (current.chartType as ChartType) : undefined,
        source = buildSource({...next, fields}, dataset),
        shapeChanged = datasetChanged || !!source.transpose !== !!prevSource?.transpose || (kind === 'chart' && !!source.latest !== !!prevSource?.latest);

    let widgetSpec = prevSpec;
    if (shapeChanged || !prevSpec || (fieldsGiven && isEmpty(fields))) {
        widgetSpec = shapeChanged || fieldsGiven || typeGiven ? specFor(kind, dataset, source, fields, chartType) : undefined;
    } else if (fieldsGiven || typeGiven) {
        widgetSpec = mergeIntoSpec(kind, prevSpec, dataset, source, fieldsGiven ? fields : null, typeGiven ? chartType : null);
    }

    // A purpose stays with the widget until it is given a new one - or shows other data
    const keptPurpose = 'purpose' in changes ? newPurpose : datasetChanged ? undefined : widget.state?.purpose;
    widget.state = withPurpose(widgetSpec ? {source, spec: widgetSpec} : {source}, keptPurpose);
    return fieldsGiven ? warning : undefined;
}

function withPurpose(state: ResearchWidgetState, purpose: string): ResearchWidgetState {
    const rest = omit(state, 'purpose');
    return purpose ? {...rest, purpose} : rest;
}

function moveWidget(spec: DashboardSpec, id: string, place: WidgetPlace, ctx: DashboardContext) {
    const widget = requireWidget(spec, id);
    check(
        PLACE_KEYS.some(k => !isNil(place[k])),
        'Give the new x, y, w or h'
    );
    validatePlace(place);

    const {layout} = widget,
        size = sizeOf(place, {width: layout.w, height: layout.h}, ctx);
    widget.layout = {x: fitX(place.x ?? layout.x, size.w, ctx), y: Math.round(place.y ?? layout.y), ...size};
    pushDown(spec.widgets, widget);
}

function replaceSpec(spec: DashboardSpec, next: DashboardSpec, ctx: DashboardContext) {
    check(isPlainObject(next) && isArray(next.widgets), 'spec must be a DashboardSpec');

    const clean = normalizeSpec(next, spec);
    clean.widgets.forEach(w => {
        check(isPlainObject(w) && isString(w.id) && WIDGET_KINDS.includes(w.kind), 'Malformed widget');
        check(isPlainObject(w.layout) && PLACE_KEYS.every(k => isNumber(w.layout[k])), `${w.id}: malformed layout`);
        if (isNil(w.state)) w.state = {};
        check(isPlainObject(w.state), `${w.id}: malformed state`);
        if (w.state.source) requireDataset(w.state.source.dataset, ctx);
    });
    check(uniq(clean.widgets.map(w => w.id)).length === clean.widgets.length, 'Widget ids must be unique');

    spec.version = clean.version;
    spec.widgets = clean.widgets;
}

//------------------------
// From a brief to a widget's saved state
//------------------------
function buildSource(brief: WidgetBrief, dataset: ResearchDataset): ResearchDataSource {
    const source: ResearchDataSource = {dataset: dataset.name};

    checkFlag(brief.latest, 'latest');
    checkFlag(brief.transpose, 'transpose');
    if (brief.transpose) {
        check(brief.kind === 'grid', 'Only a grid can show one line per field (transpose)');
        source.transpose = true;
    }
    if (brief.latest) check(dataset.supportsLatest, `${dataset.name} is not a snapshot per date, so 'latest' does not apply - filter or sort it instead`);
    // A transposed grid shows the newest row only, so there is no need to load the others
    if (brief.latest || (source.transpose && dataset.supportsLatest)) source.latest = true;

    const filters = validateFilters(brief.filters, dataset);
    if (!isEmpty(filters)) source.filters = filters;
    if (source.transpose && !isEmpty(brief.fields)) source.fields = [...brief.fields];
    return source;
}

/** The spec for a brief - undefined when the widget's default spec is what the brief asks for. */
function specFor(kind: ResearchWidgetKind, dataset: ResearchDataset, source: ResearchDataSource, fields: string[], chartType: ChartType): GridSpec | ChartSpec {
    if (kind === 'grid') return source.transpose || isEmpty(fields) ? undefined : gridSpecFor(dataset, source, fields);

    const spec = chartSpecFor(dataset, source, fields, chartType);
    check(spec, `${dataset.name} has no numeric fields to chart - show it as a grid`);
    return isEmpty(fields) && !chartType ? undefined : spec;
}

/** Change the fields or the chart type of a spec the user may have tuned, keeping what it says about the fields that stay. */
function mergeIntoSpec(
    kind: ResearchWidgetKind,
    prev: GridSpec | ChartSpec,
    dataset: ResearchDataset,
    source: ResearchDataSource,
    fields: string[],
    chartType: ChartType
): GridSpec | ChartSpec {
    if (kind === 'grid') {
        const grid = prev as GridSpec;
        if (source.transpose || !isArray(grid.columns) || isEmpty(fields)) return prev;

        const existing = leafColumns(grid),
            columns = leafColumns(gridSpecFor(dataset, source, fields)).map(
                c => omit(existing.find(e => e.field === c.field) ?? c, 'hidden') as GridSpecColumn
            );
        return {...grid, columns, sortBy: (grid.sortBy ?? []).filter(s => columns.some(c => c.field === s.colId))};
    }

    const chart = cloneDeep(prev as ChartSpec);
    if (!isArray(chart.series)) return prev;

    const type = chartType ?? chart.type,
        oneAxis = type === 'pie' || type === 'bar' || chart.layout !== 'series';
    if (!isEmpty(fields)) {
        const byName = (name: string) => dataset.fields.find(f => f.name === name),
            first = byName(fields[0]);
        chart.series = fields.map(
            name => omit(chart.series.find(s => s.field === name) ?? chartSeriesFor(byName(name), first, oneAxis), 'hidden') as ChartSpecSeries
        );

        // A field that lands on the second axis needs that axis described, if it wasn't in use
        const other = fields.map(byName).find(f => chart.series.find(s => s.field === f.name)?.axis === 'secondary');
        if (other && isEmpty(chart.axes?.secondary)) chart.axes = {...chart.axes, secondary: unitAxis(other)};
    }
    if (chartType) {
        chart.type = chartType;
        if (chartType === 'pie' || chartType === 'bar') chart.series.forEach(s => delete s.type);
    }
    return chart;
}

//------------------------
// Placing widgets
//------------------------
function insert(
    spec: DashboardSpec,
    widget: {kind: ResearchWidgetKind; title: string; state: ResearchWidgetState},
    place: WidgetPlace,
    defaultSize: {width: number; height: number},
    ctx: DashboardContext,
    flowed: Set<string>
) {
    validatePlace(place);
    const size = sizeOf(place, defaultSize, ctx),
        // Placed where asked - otherwise in the first free space, reading left to right and then down
        explicit = !isNil(place.x) && !isNil(place.y),
        at = explicit ? {x: fitX(place.x, size.w, ctx), y: Math.round(place.y)} : firstFree(spec.widgets, size, ctx),
        added: DashboardWidget = {id: newId(spec), ...widget, layout: {...at, ...size}};

    spec.widgets.push(added);
    if (explicit) pushDown(spec.widgets, added);
    else flowed.add(added.id);
}

function validatePlace(place: WidgetPlace) {
    PLACE_KEYS.forEach(k => check(isNil(place[k]) || (isNumber(place[k]) && place[k] >= 0), `${k} must be a number, 0 or more`));
}

function sizeOf(place: WidgetPlace, defaults: {width: number; height: number}, ctx: DashboardContext): {w: number; h: number} {
    return {
        w: clamp(Math.round(place.w ?? defaults.width), MIN_WIDGET_SIZE, ctx.capabilities.columns),
        h: clamp(Math.round(place.h ?? defaults.height), MIN_WIDGET_SIZE, MAX_WIDGET_HEIGHT)
    };
}

function fitX(x: number, w: number, ctx: DashboardContext): number {
    return clamp(Math.round(x), 0, ctx.capabilities.columns - w);
}

function overlaps(a: DashboardWidgetLayout, b: DashboardWidgetLayout): boolean {
    return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

function firstFree(widgets: DashboardWidget[], size: {w: number; h: number}, ctx: DashboardContext): {x: number; y: number} {
    const bottom = max(widgets.map(w => w.layout.y + w.layout.h)) ?? 0;
    for (let y = 0; y < bottom; y++) {
        for (let x = 0; x + size.w <= ctx.capabilities.columns; x++) {
            if (!widgets.some(w => overlaps(w.layout, {x, y, ...size}))) return {x, y};
        }
    }
    return {x: 0, y: bottom};
}

/**
 * Lay out the widgets added without an x and y, in the order they were added. One that fits a gap
 * among the other widgets goes there, at its size; the rest go in rows below them, each row filled
 * to the canvas width - widths scaled up in proportion - with its widgets at the same height. The
 * sizes the assistant gives then only need to be roughly right.
 */
function arrangeFlowed(spec: DashboardSpec, ids: Set<string>, ctx: DashboardContext) {
    const {columns} = ctx.capabilities,
        placed = spec.widgets.filter(w => !ids.has(w.id)),
        bottom = max(placed.map(w => w.layout.y + w.layout.h)) ?? 0,
        rows: DashboardWidget[][] = [];

    spec.widgets
        .filter(w => ids.has(w.id))
        .forEach(w => {
            const gap = firstFree(placed, w.layout, ctx);
            if (gap.y + w.layout.h <= bottom) {
                w.layout = {...w.layout, ...gap};
                placed.push(w);
                return;
            }
            const row = rows[rows.length - 1];
            if (row && sumBy(row, it => it.layout.w) + w.layout.w <= columns) row.push(w);
            else rows.push([w]);
        });

    let y = bottom;
    rows.forEach(row => {
        const total = sumBy(row, w => w.layout.w),
            h = max(row.map(w => w.layout.h)),
            widths = row.map(w => Math.floor((w.layout.w * columns) / total));
        // What rounding down left over goes to the widest
        widths[widths.indexOf(max(widths))] += columns - sumBy(widths, it => it);

        let x = 0;
        row.forEach((w, idx) => {
            w.layout = {x, y, w: widths[idx], h};
            x += widths[idx];
        });
        y += h;
    });
}

/** Move the widgets a placed widget now covers below it - and whatever those then cover, in turn. */
function pushDown(widgets: DashboardWidget[], placed: DashboardWidget) {
    const queue = [placed];
    while (queue.length) {
        const fixed = queue.shift();
        widgets.forEach(w => {
            if (w === fixed || w === placed || !overlaps(w.layout, fixed.layout)) return;
            w.layout.y = fixed.layout.y + fixed.layout.h;
            queue.push(w);
        });
    }
}

function newId(spec: DashboardSpec): string {
    let n = 1;
    while (spec.widgets.some(w => w.id === `new_${n}`)) n++;
    return `new_${n}`;
}

//------------------------
// Validation
//------------------------
function findDataset(name: string, ctx: DashboardContext): ResearchDataset {
    return ctx.capabilities.datasets.find(d => d.name === name) ?? null;
}

function requireDataset(name: string, ctx: DashboardContext): ResearchDataset {
    check(isString(name) && !isEmpty(name), 'dataset is required');
    const dataset = findDataset(name, ctx);
    check(dataset, `Unknown dataset '${name}'`);
    return dataset;
}

function requireWidget(spec: DashboardSpec, id: string): DashboardWidget {
    const widget = spec.widgets.find(w => w.id === id);
    check(widget, `No widget '${id}' on the dashboard`);
    return widget;
}

function validatePurpose(purpose: string): string {
    if (isNil(purpose)) return undefined;
    check(isString(purpose), 'purpose must be text');
    return purpose.trim() || undefined;
}

function validateTitle(title: string): string {
    if (isNil(title)) return undefined;
    check(isString(title) && !isEmpty(title.trim()), 'title must be text');
    return title.trim();
}

function checkFlag(v: any, name: string) {
    check(isNil(v) || isBoolean(v), `${name} must be true or false`);
}

function validateChartType(chartType: string, kind: ResearchWidgetKind): ChartType {
    if (isNil(chartType)) return undefined;
    check(kind === 'chart', 'Only a chart has a chartType');
    check(BRIEF_CHART_TYPES.includes(chartType as any), `chartType must be one of ${BRIEF_CHART_TYPES.join(', ')}`);
    return chartType as ChartType;
}

const NO_FIELDS: {fields: string[]; warning?: string} = {fields: undefined};

/**
 * The fields, checked against the dataset. Fields the dataset doesn't have - or that a chart can't
 * plot - are left out with a warning, as a widget of the rest serves better than none: asked for
 * figures of two datasets, the assistant tends to list them all under one. With no usable field
 * left the op is refused. A chart's time field is its x axis, so it is dropped silently.
 */
function validateFields(fields: string[], dataset: ResearchDataset, kind: ResearchWidgetKind, title: string): {fields: string[]; warning?: string} {
    if (isNil(fields)) return NO_FIELDS;
    check(isArray(fields) && fields.every(isString), 'fields must be a list of field names');

    const names = uniq(fields).filter(name => name !== TICKER_FIELD && !(kind === 'chart' && name === dataset.timeField)),
        usable = names.filter(name => {
            const field = dataset.fields.find(f => f.name === name);
            return field && (kind === 'grid' || isMeasure(field));
        }),
        unusable = names.filter(name => !usable.includes(name));
    if (isEmpty(unusable)) return {fields: usable};

    const reasons = unusable.map(name =>
        dataset.fields.some(f => f.name === name) ? `'${name}' is not a number and cannot be plotted` : `Unknown field '${name}' in ${dataset.name}`
    );
    check(!isEmpty(usable), reasons.join('; '));
    return {fields: usable, warning: `${title}: left out ${unusable.join(', ')} - not ${kind === 'chart' ? 'numbers ' : ''}in ${dataset.title}`};
}

function validateFilters(filters: ResearchDataFilter[], dataset: ResearchDataset): ResearchDataFilter[] {
    if (isNil(filters)) return [];
    check(isArray(filters), 'filters must be a list');

    return filters.map(f => {
        check(isPlainObject(f), 'Malformed filter');
        const field = f.field !== TICKER_FIELD ? dataset.fields.find(d => d.name === f.field) : null;
        check(field, f.field === TICKER_FIELD ? 'The ticker is set by the dashboard - it cannot be filtered' : `Unknown field '${f.field}' in ${dataset.name}`);
        check(FILTER_OPS.includes(f.op), `Unknown filter op '${f.op}' - use one of ${FILTER_OPS.join(', ')}`);

        if (f.op === 'isNull' || f.op === 'isNotNull') return {field: f.field, op: f.op};
        check(!isNil(f.value) && f.value !== '', `The filter on ${f.field} needs a value`);
        if (f.op === 'in') check(isArray(f.value) && !isEmpty(f.value), `The 'in' filter on ${f.field} needs a list of values`);

        // An enum's values are known - anything else would silently match no rows
        if (field.enumValues && ['eq', 'ne', 'in'].includes(f.op)) {
            const unknown = [].concat(f.value).find(v => !field.enumValues.includes(v));
            check(isNil(unknown), `${f.field} is one of ${field.enumValues.join(', ')} - not '${unknown}'`);
        }
        return {field: f.field, op: f.op, value: cloneDeep(f.value)};
    });
}
